<script setup lang="ts">
/**
 * This component wraps SurfacePostBeingShowed
 * SurfacePostBeingShowed should have the logic and vuex actions that related to the post
 * and interactions on the post itself:
 * - modify content of the post (including reactions, comments, moderation)
 * - change the toggle status of panels and dropdowns
 * This component should only have the logic and vuex actions that related to post appearance
 * and the interactions between posts:
 * - decide classes and styles
 * - modify size/position of post
 * - modify connections for freeform format
 */
import device from '@@/bits/device'
import { isUrl } from '@@/bits/url'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import type { Id, Post } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostBeingShowed from '@@/vuecomponents/SurfacePostBeingShowed.vue'
import { useSurfacePostHoverHandlers } from '@@/vuecomposables/useSurfacePostHoverHandlers'
import { storeToRefs } from 'pinia'
import { computed, ref, toRef } from 'vue'

const PostConnectionOverlay = defineAsyncComponent(() => import('@@/vuecomponents/PostConnectionOverlay.vue'))

const {
  index,
  post,
  putAttachmentOnTop = false,
  xShadow = true,
  postReactionIds = [],
  lazyRender = false,
  minPostHeight = 0,
  minPostWidth = 0,
} = defineProps<{
  index: number
  post: Post
  putAttachmentOnTop?: boolean
  xShadow?: boolean
  postReactionIds?: Id[]
  lazyRender?: boolean
  minPostHeight?: number
  minPostWidth?: number
}>()

const emit = defineEmits<{
  (e: 'focus'): void
}>()

// Pinia stores
const surfacePostActionStore = useSurfacePostActionStore()
const { postUnderActionCid, postBeingTransferredCid, postBeingCopiedCid } = storeToRefs(surfacePostActionStore)
const { connections, postToConnectFromId } = storeToRefs(useSurfacePostConnectionStore())
const { canIModerate, canIWrite } = storeToRefs(useSurfacePermissionsStore())
const { xAuthor, isStream } = storeToRefs(useSurfaceStore())
const { postWithLocationBeingEditedCid } = storeToRefs(useSurfaceMapStore())
const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())

// Local states
const label = ref('')

const isBeingActedUpon = computed(() => {
  return (
    postBeingTransferredCid.value === post.cid ||
    postBeingCopiedCid.value === post.cid ||
    postToConnectFromId.value === post.id ||
    postWithLocationBeingEditedCid.value === post.cid
  )
})

const isAppActingOnPost = computed(() => {
  return device.app && !!postUnderActionCid.value && String(post.id) === String(postUnderActionCid.value)
})

const wasWrittenByCurrentUser = computed(() => {
  return !!currentUser.value?.id && post.author_id === currentUser.value?.id
})

const isEditableByCurrentUser = computed(() => {
  return canIModerate.value || (wasWrittenByCurrentUser.value && canIWrite.value)
})

const hasAttachment = computed(() => {
  const attachment = post.attachment
  return !!attachment && isUrl(attachment)
})

const xConnect = computed(() => {
  return (
    !!postToConnectFromId.value &&
    postToConnectFromId.value !== post.id &&
    post.id != null &&
    !areConnected(post.id, postToConnectFromId.value)
  )
})

const classes = computed(() => ({
  'surface-post': true,
  wish: true,
  'select-none': true,
  'rounded-2xl': isStream.value,
  'rounded-xl': !isStream.value,
  'is-animated': isBeingActedUpon.value,
  pulsate: isBeingActedUpon.value || isAppActingOnPost.value,
  'wish-editable': isEditableByCurrentUser.value,
  'wish-mine': wasWrittenByCurrentUser.value,
  'with-attachment': hasAttachment.value,
  'with-author': xAuthor.value,
  'attachment-on-top': putAttachmentOnTop,
  'shadow-elevation-1': xShadow,
}))

// Methods
const areConnected = (post1Id: number, post2Id: number): boolean => {
  return (
    connections.value.filter((c) => {
      return (
        (c.from_wish_id === post1Id && c.to_wish_id === post2Id) ||
        (c.from_wish_id === post2Id && c.to_wish_id === post1Id)
      )
    }).length > 0
  )
}

const handleConnect = (labelText: string): void => {
  label.value = labelText
  selectToConnect()
}

const selectToConnect = (): void => {
  const labelText = label.value
  if (postToConnectFromId.value != null && post.id != null) {
    useSurfacePostConnectionStore().completeConnection({ postToConnectToId: post.id, label: labelText ?? '' })
  }
}

const { onPostMouseEnter, onPostMouseLeave } = useSurfacePostHoverHandlers(toRef(() => post))
</script>

<template>
  <SurfaceLazy
    v-if="lazyRender"
    :id="'wish-' + (post.id || 'new')"
    :min-height="minPostHeight"
    :min-width="minPostWidth"
    :class="classes"
    :data-view-mode="'show'"
    :data-post-cid="post.cid"
    :data-pinned="String(!!post.pinned_at)"
    :data-rank="post.sort_index"
    :data-index="index + 1"
    :data-section-id="post.wall_section_id"
    :data-color="post.color"
    @mouseenter="onPostMouseEnter"
    @mouseleave="onPostMouseLeave"
  >
    <SurfacePostBeingShowed
      v-bind="{ index, post, putAttachmentOnTop, xShadow, postReactionIds, lazyRender, minPostHeight, minPostWidth }"
      @focus="emit('focus')"
    />
    <PostConnectionOverlay v-if="xConnect" :edit-mode="false" @connect="handleConnect" />
  </SurfaceLazy>
  <div
    v-else
    :id="'wish-' + (post.id || 'new')"
    :class="classes"
    :data-view-mode="'show'"
    :data-post-cid="post.cid"
    :data-pinned="String(!!post.pinned_at)"
    :data-rank="post.sort_index"
    :data-index="index + 1"
    :data-section-id="post.wall_section_id"
    :data-color="post.color"
    @mouseenter="onPostMouseEnter"
    @mouseleave="onPostMouseLeave"
  >
    <SurfacePostBeingShowed
      v-bind="{ index, post, putAttachmentOnTop, xShadow, postReactionIds, lazyRender, minPostHeight, minPostWidth }"
      @focus="emit('focus')"
    />
    <PostConnectionOverlay v-if="xConnect" :edit-mode="false" @connect="handleConnect" />
  </div>
</template>

<style lang="scss">
.attachment-on-top {
  .real-wish {
    display: flex;
    flex-direction: column;

    .surface-post-attachment {
      order: -1;
      flex-shrink: 0;
    }
    .surface-post-author {
      order: -2;
      flex-shrink: 0;
    }
    .words {
      flex-grow: 1;
    }
  }
}

.attachment-on-top.with-attachment {
  .real-wish {
    .author + .words {
      padding-top: 12px;
    }
  }
}

.post-highlight-flash {
  animation: post-highlight-flash 3s ease-out;
}

html[data-reduced-motion-mode='true'] .post-highlight-flash {
  animation: none;
}

/* The flash box-shadow follows the element's border-radius. Table rows (`.post-table-row`) are square,
   so round the ring for them to match the rounded card treatment in every other layout. */
.post-table-row.post-highlight-flash {
  border-radius: 0.75rem;
}

@keyframes post-highlight-flash {
  0% {
    box-shadow: 0 0 0 3px rgb(124 58 237 / 0.7);
  }
  50% {
    box-shadow: 0 0 0 3px rgb(124 58 237 / 0.5);
  }
  100% {
    box-shadow: 0 0 0 3px rgb(124 58 237 / 0);
  }
}

[data-color-scheme='dark'] .post-highlight-flash {
  animation: post-highlight-flash-dark 3s ease-out;
}

@keyframes post-highlight-flash-dark {
  0% {
    box-shadow: 0 0 0 3px rgb(250 204 21 / 0.7);
  }
  50% {
    box-shadow: 0 0 0 3px rgb(250 204 21 / 0.5);
  }
  100% {
    box-shadow: 0 0 0 3px rgb(250 204 21 / 0);
  }
}
</style>

<style lang="scss" scoped>
.full-height {
  height: 100%;
}

// need this so dragging on fire won't leave a weird trail: https://www.loom.com/share/e5f2cd1ea25d4021b7d2c20559e1318d
// looks like it's caused by rotating the container without rotating the child so the child is rendered in a weird way
// => rotate the first child slightly so it will be rendered together with the container
.wish.ui-sortable-helper {
  .firefox & {
    & > :first-child {
      transform: rotate(0.1deg);
    }
  }
}
</style>
